import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { RotateCcw, Loader2, PlusSquareIcon, PencilIcon, CheckIcon, XIcon, BookmarkIcon, SendIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HistoryItem } from '../types';
import SuggestionInput from './SuggestionInput';
import CustomRulesDialog from './CustomRulesDialog';
import { MyVoiceWrapper } from './MyVoiceDialog';
import { useMutation } from '@apollo/client';
import {
  CREATE_USER_COMPLETION_TYPE_RULE,
  UPDATE_USER_COMPLETION_TYPE_RULE,
  UPDATE_COMPLETION,
  CREATE_SAVE,
} from '../graphql/mutations';
import { COMPLETION_TYPES_QUERY, IMAGE_QUERY, USER_SAVES_QUERY } from '../graphql/queries';
import Editor from './Editor';
import { formatDate, getPublicImageUrl } from '../utils';
import AddLabel from './AddLabel';
import { Label } from '../types';
import CopyButton from './CopyButton';
import { FetchPolicy } from '@apollo/client';
import { COLLECTION_QUERY, SERIES_QUERY } from '../graphql/queries';
import { useAnalytics } from '~/context/analytics';
import ShareModal from '~/components/ShareModal';
import { useAuth } from '~/context/auth';

interface WriterProps {
  hideControls: boolean;
  completionType: any;
  sparkResponse: string;
  completion: any;
  loading: boolean;
  suggestion: string;
  history: HistoryItem[];
  editedContent: string | null;
  isTyping: boolean;
  setEditedContent: (content: string | null) => void;
  setHistory: (history: HistoryItem[]) => void;
  setSuggestion: (suggestion: string) => void;
  onRestart: () => void;
  onSubmitSuggestion: () => void;
}

const INTEGRATIONS_ENABLED = import.meta.env.VITE_INTEGRATIONS_ENABLED === 'true';

function ContentWriter({
  hideControls = false,
  completionType,
  sparkResponse,
  isTyping,
  completion,
  loading,
  suggestion,
  history,
  editedContent,
  setEditedContent,
  setHistory,
  setSuggestion,
  onRestart,
  onSubmitSuggestion,
}: WriterProps) {
  const { t } = useTranslation('social');
  const { t: tc } = useTranslation('common');
  const [showHistory, setShowHistory] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editCanceled, setEditCanceled] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [shareModalState, setShareModalState] = useState({
    isOpen: false,
    url: '',
    title: '',
    quote: '',
  });
  const cardContentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { amplitude, intercom } = useAnalytics();
  const { profile: userProfile } = useAuth();

  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsCompactView(entry.contentRect.width < 476);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const [updateRule] = useMutation(UPDATE_USER_COMPLETION_TYPE_RULE, {
    refetchQueries: [{ query: COMPLETION_TYPES_QUERY }],
  });

  const [createRule] = useMutation(CREATE_USER_COMPLETION_TYPE_RULE, {
    refetchQueries: [{ query: COMPLETION_TYPES_QUERY }],
  });

  const [updateCompletion] = useMutation(UPDATE_COMPLETION);

  const [createSave] = useMutation(CREATE_SAVE, {
    refetchQueries: [
      { query: USER_SAVES_QUERY },
      ...(completion?.targetType === 'collection' && completion?.targetId
        ? [
            {
              query: COLLECTION_QUERY,
              variables: { id: completion.targetId },
              fetchPolicy: 'network-only' as FetchPolicy,
            },
          ]
        : completion?.targetType === 'series' && completion?.targetId
          ? [
              {
                query: SERIES_QUERY,
                variables: { id: completion.targetId },
                fetchPolicy: 'network-only' as FetchPolicy,
              },
            ]
          : completion?.targetType === 'image' && completion?.targetId
            ? [
                {
                  query: IMAGE_QUERY,
                  variables: { id: completion.targetId },
                  fetchPolicy: 'network-only' as FetchPolicy,
                },
              ]
            : []),
    ],
  });

  useEffect(() => {
    if (showHistory && cardContentRef.current) {
      cardContentRef.current.scrollTo({
        top: cardContentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [history, showHistory]);

  const renderStreamedResponse = () => {
    if (!showHistory) {
      if (loading && !sparkResponse) {
        return (
          <div className="flex items-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mx-2" />
            <span className="animate-pulse italic">{t('content-writer.thinking')}</span>
          </div>
        );
      } else {
        return (
          <>
            <Editor
              content={sparkResponse}
              onUpdate={(content: string | null) => {
                setEditedContent(content);
              }}
              isEditing={isEditing}
              editCanceled={editCanceled}
            />
            <div className="h-[40px]"></div>
          </>
        );
      }
    }
  };

  const onAddToCustomRule = async (suggestion: string) => {
    try {
      if (completionType?.userRules?.id) {
        // Update existing rule
        await updateRule({
          variables: {
            input: {
              id: completionType.userRules.id,
              rules: completionType.userRules.rules + `\n${suggestion}`,
              active: completionType.userRules.active,
            },
          },
        });
      } else {
        // Create new rule
        await createRule({
          variables: {
            input: {
              completionTypeId: completionType.id,
              rules: suggestion,
              active: true,
            },
          },
        });
      }

      toast.success(t('content-writer.custom-rules-updated'));
    } catch (error) {
      console.error('Error saving custom rules:', error);
      toast.error(t('content-writer.custom-rules-save-failed'));
    }
  };

  const renderHistory = () => {
    if (showHistory && history.length > 0) {
      return (
        <div className="flex flex-col">
          {history.map((item, index) => (
            <div key={item.id} className="mb-6">
              {item.suggestion?.content && (
                <div className="flex justify-end mb-4">
                  <div className="text-sm text-right inline-block ml-auto bg-gray-100 p-4 rounded-lg mb-2">
                    <div>{item.suggestion.content}</div>
                    {renderAddToCustomRuleButton(item.suggestion.content)}
                    <CopyButton text={item.suggestion.content} historyItem={true} loading={loading} />
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500 flex justify-between items-center mb-2">
                <div>
                  {index === history.length - 1
                    ? t('content-writer.latest')
                    : formatDate(new Date(parseInt(item.createdAt)))}
                </div>
              </div>
              <Editor content={item.responseHtml || item.response} isEditing={false} onUpdate={() => {}} />
              <CopyButton text={item.responseHtml || item.response} historyItem={true} loading={loading} />
            </div>
          ))}
        </div>
      );
    }
  };

  const renderRestartButton = () => {
    return (
      <Button
        title={t('content-writer.clear-session-title')}
        variant="ghost"
        className="h-6 text-xs whitespace-normal text-left"
        size="sm"
        disabled={loading || isEditing || isTyping}
        onClick={(e) => {
          e.preventDefault();
          amplitude.track('spark_content_regenerated', {
            spark_type: completionType?.name,
          });
          intercom.trackEvent('spark_content_regenerated', {
            spark_type: completionType?.name,
          });
          onRestart();
        }}
        data-amp-unmask
      >
        <RotateCcw className="size-4" />
        {t('content-writer.clear-session')}
      </Button>
    );
  };

  const renderAddToCustomRuleButton = (text?: string) => {
    return (
      <Button
        title={t('content-writer.add-to-custom-rule-title')}
        type="button"
        variant="ghost"
        className="h-6 text-xs relative text-gray-500 px-0 hover:bg-transparent mr-6"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          const rule = text || sparkResponse;
          if (rule) {
            onAddToCustomRule(rule);
          }
        }}
        disabled={loading}
      >
        <PlusSquareIcon className="w-4 h-4 mr-2" />
        {t('content-writer.add-to-custom-rules')}
      </Button>
    );
  };

  const renderShowHistoryCheckbox = () => {
    return (
      <div title={t('content-writer.show-history-title')} className="flex items-center space-x-2">
        <Checkbox
          id="show-history"
          onCheckedChange={() => {
            setShowHistory(!showHistory);
          }}
          disabled={isEditing}
        />
        <label
          htmlFor="show-history"
          className="text-xs font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
        >
          {t('content-writer.show-history')}
        </label>
      </div>
    );
  };

  const handleEditDone = async () => {
    setIsEditing(false);

    amplitude.track('spark_content_edited', {
      spark_type: completionType?.name,
    });

    intercom.trackEvent('spark_content_edited', {
      spark_type: completionType?.name,
    });

    await updateCompletion({
      variables: {
        input: {
          id: completion.id,
          responseHtml: editedContent,
        },
      },
    });

    if (editedContent) {
      const updatedHistory = history.map((item) => {
        if (item.id === completion.id) {
          return {
            ...item,
            responseHtml: editedContent,
          };
        }
        return item;
      });

      setHistory(updatedHistory);
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditCanceled(true);

    setTimeout(() => {
      setEditCanceled(false);
    }, 500);

    setEditedContent(null);
  };

  const handleSave = async () => {
    try {
      await createSave({
        variables: {
          input: {
            completionId: completion.id,
            content: sparkResponse,
            labels: selectedLabels.map((label) => label.id),
          },
        },
      });

      amplitude.track('spark_content_saved', {
        spark_type: completionType?.name,
      });

      intercom.trackEvent('spark_content_saved', {
        spark_type: completionType?.name,
      });

      toast.success(t('content-writer.saved'));
      setSaveDialogOpen(false);
      setSelectedLabels([]);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(t('content-writer.save-failed'));
    }
  };

  const renderSaveDialog = () => {
    return (
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-row justify-start items-center">
            <DialogTitle className="flex flex-row justify-start items-center mb-0">
              <BookmarkIcon className="size-6 mr-2" /> {t('content-writer.save-response')}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>{t('content-writer.save-response-description')}</DialogDescription>
          <div className="flex flex-col justify-start mt-4">
            <AddLabel
              selectedLabels={selectedLabels}
              onSelect={(label) => {
                setSelectedLabels((prev) => [...prev, label]);
              }}
              onRemove={(label) => {
                setSelectedLabels((prev) => prev.filter((l) => l.id !== label.id));
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSaveDialogOpen(false);
                setSelectedLabels([]);
              }}
            >
              {tc('cancel')}
            </Button>
            <Button onClick={handleSave}>{tc('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const showPostButton = INTEGRATIONS_ENABLED && completionType?.name?.toLowerCase().includes('social post');

  const handlePostClick = () => {
    amplitude.track('new_post_clicked', {
      location: 'spark',
    });

    intercom.trackEvent('new_post_clicked', {
      location: 'spark',
    });

    // Get public URL for sharing if we have image data
    if (completion?.image?.slug && userProfile?.profile?.slug) {
      const publicImageUrl = getPublicImageUrl(userProfile.profile.slug, completion.image.slug);
      const urlWithUtm = `${publicImageUrl}?utm_source=arthelper&utm_term=social_post_spark_share_link`;

      setShareModalState({
        isOpen: true,
        url: urlWithUtm,
        title: completion.image.title || t('content-writer.check-out-artwork'),
        quote: completion.image.title || t('content-writer.check-out-artwork'),
      });
    } else {
      // Fallback: navigate directly if no image data
      navigate('/post', {
        state: {
          completion: completion,
          from: window.location.pathname,
        },
      });
    }
  };

  const handlePostToInstagram = () => {
    navigate('/post', {
      state: {
        completion: completion,
        from: window.location.pathname,
      },
    });
  };

  return (
    <div ref={containerRef} className="w-full md:max-w-[768px] space-y-4 relative p-8 pt-4">
      <Card className="min-h-[186px] relative rounded-md">
        <CardHeader className="flex w-full flex-row justify-between items-center space-y-0 px-6 pt-4 pb-0 mb-4">
          {!hideControls && (
            <>
              <div>
                <CustomRulesDialog completionType={completionType} isDisabled={isEditing} compact={isCompactView} />
              </div>
            </>
          )}
          {!hideControls && (
            <div className="flex items-center space-x-2 justify-end">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setIsEditing(true)}
                  disabled={loading || showHistory}
                >
                  <PencilIcon className="size-4" />
                  {!isCompactView && <span>{tc('edit')}</span>}
                </Button>
              ) : (
                <>
                  <Button size="lg" className="text-red-500" variant="link" onClick={handleEditCancel}>
                    <XIcon className="size-4" /> {!isCompactView && <span>{tc('cancel')}</span>}
                  </Button>
                  <Button variant="outline" size="lg" onClick={handleEditDone}>
                    <CheckIcon className="size-4" />
                    {!isCompactView && <span>{tc('done')}</span>}
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="lg"
                disabled={isEditing || loading || showHistory}
                onClick={() => setSaveDialogOpen(true)}
              >
                <BookmarkIcon className="size-4" />
                {!isCompactView && <span>{tc('save')}</span>}
              </Button>
              {showPostButton && (
                <Button
                  size="lg"
                  className="bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={handlePostClick}
                  disabled={isEditing || loading || showHistory}
                >
                  <SendIcon className="size-4" />
                  {!isCompactView && <span>{tc('post')}</span>}
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent ref={cardContentRef} className="h-[340px] md:h-[400px] overflow-y-auto py-0">
          {renderHistory()}
          {renderStreamedResponse()}
        </CardContent>
        {!showHistory && (
          <CardFooter className="pt-0">
            <SuggestionInput
              suggestion={suggestion}
              setSuggestion={setSuggestion}
              loading={loading}
              onSubmitSuggestion={onSubmitSuggestion}
              isDisabled={isEditing}
            />
          </CardFooter>
        )}
      </Card>
      <div className="flex justify-between items-center">
        <div className="space-x-2 flex">
          {renderShowHistoryCheckbox()}
          {!showHistory && renderRestartButton()}
        </div>
        {!showHistory && <CopyButton text={isEditing ? editedContent : completion?.responseHtml} loading={loading} />}
      </div>
      {!hideControls && (
        <div className="-ml-3">
          <MyVoiceWrapper />
        </div>
      )}
      {renderSaveDialog()}
      <ShareModal
        isOpen={shareModalState.isOpen}
        onClose={() => {
          setShareModalState({
            isOpen: false,
            url: '',
            title: '',
            quote: '',
          });
        }}
        url={shareModalState.url}
        title={shareModalState.title}
        quote={shareModalState.quote}
        showPostToInstagram={true}
        onPostToInstagram={handlePostToInstagram}
      />
    </div>
  );
}

export default ContentWriter;
